import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApiAuthed, useApiDev } from "../auth.js";
import type { OverviewResponse } from "../api.js";
import { loadGeoSelection, type GeoSelection } from "../components/GeoSelector.js";

// Phase 26: mobile-only signal stack. One signal at a time. Swipe up
// for next, down for previous, tap to open the query in Command
// Center. Pure UI — every signal is sourced from the existing
// /v1/overview payload (recs + gaps + near-impact).

interface PageProps {
  devModeNoAuth?: boolean;
}

type SignalTag = "CRITICAL" | "GAP" | "MOVEMENT" | "POLICY" | "LEGAL" | "WORKFORCE";

interface Signal {
  id: string;
  tag: SignalTag;
  headline: string;
  subtext: string;
  module: string;
  query: string;
}

export function SignalsFeedPage({ devModeNoAuth }: PageProps) {
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const [geo] = useState<GeoSelection>(() => loadGeoSelection());
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    setData(null);
    api
      .overview("7d", geo)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(String(e));
      });
    return () => {
      alive = false;
    };
  }, [api, geo]);

  const signals = useMemo<Signal[]>(() => buildSignals(data), [data]);

  return (
    <div className="bios-feed">
      {error && <p className="bios-feed-error">{error}</p>}
      {!error && !data && <p className="bios-feed-loading">Listening...</p>}
      {data && signals.length === 0 && (
        <p className="bios-feed-empty">No live signals.</p>
      )}

      {signals.length > 0 && <SignalStack signals={signals} />}

      <BottomNav active="signals" />
      <div className="bios-feed-bottomnav-spacer" aria-hidden />
    </div>
  );
}

// ---- SignalStack -------------------------------------------------------

const SWIPE_THRESHOLD = 80; // px before we commit to a card change
const TAP_TOLERANCE = 6;    // px movement allowed for a tap to register
const LONG_PRESS_MS = 520;

function SignalStack({ signals }: { signals: Signal[] }) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [drag, setDrag] = useState<{ y0: number; dy: number } | null>(null);
  const [actionSignal, setActionSignal] = useState<Signal | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(() => loadSavedSignalIds());
  const cardRef = useRef<HTMLDivElement | null>(null);
  const longPressRef = useRef<number | null>(null);

  const visible = signals.slice(index, index + 1);
  const hasNext = index < signals.length - 1;
  const hasPrev = index > 0;

  function commitDrag(dy: number) {
    if (dy < -SWIPE_THRESHOLD && hasNext) setIndex((i) => i + 1);
    else if (dy > SWIPE_THRESHOLD && hasPrev) setIndex((i) => i - 1);
    setDrag(null);
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    setDrag({ y0: e.clientY, dy: 0 });
    const signal = signals[index];
    if (signal) {
      longPressRef.current = window.setTimeout(() => {
        setActionSignal(signal);
        longPressRef.current = null;
      }, LONG_PRESS_MS);
    }
    try {
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* ignore — capture is best-effort */
    }
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!drag) return;
    const dy = e.clientY - drag.y0;
    if (Math.abs(dy) > TAP_TOLERANCE && longPressRef.current !== null) {
      window.clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
    setDrag({ y0: drag.y0, dy });
  }
  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (!drag) return;
    if (longPressRef.current !== null) {
      window.clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
    if (actionSignal) {
      setDrag(null);
      return;
    }
    const wasTap = Math.abs(drag.dy) <= TAP_TOLERANCE;
    if (wasTap) {
      const top = signals[index];
      if (top) {
        navigate(
          `/app/command?q=${encodeURIComponent(top.query)}&signal=${encodeURIComponent(top.headline)}`,
        );
      }
      setDrag(null);
      return;
    }
    commitDrag(drag.dy);
  }
  function onPointerCancel() {
    if (longPressRef.current !== null) {
      window.clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
    setDrag(null);
  }

  function saveSignal(signal: Signal) {
    const next = new Set(savedIds);
    next.add(signal.id);
    setSavedIds(next);
    saveSignalIds(next);
    setActionSignal(null);
  }

  function investigateLater(signal: Signal) {
    saveSignal(signal);
  }

  // Reset index if the signals list shrinks under our pointer.
  useEffect(() => {
    if (index >= signals.length) setIndex(Math.max(0, signals.length - 1));
  }, [index, signals.length]);

  return (
    <div
      className="bios-stack"
      role="region"
      aria-label="Live signals — swipe to advance"
    >
      {/* Exact home contract: one signal only. */}
      {[...visible].reverse().map((card, reversedIdx) => {
        const depth = visible.length - 1 - reversedIdx; // 0 = active, 1 = behind, 2 = farther
        const isActive = depth === 0;
        const transform = stackTransform(depth, isActive ? drag?.dy ?? 0 : 0);
        const opacity = stackOpacity(depth);
        return (
          <div
            key={card.id}
            ref={isActive ? cardRef : null}
            className={`bios-stack-card${isActive ? " bios-stack-card-active" : ""} bios-stack-card-${card.tag.toLowerCase()}`}
            style={{
              transform,
              opacity,
              transition: drag ? "none" : undefined,
              touchAction: isActive ? "none" : "auto",
            }}
            onPointerDown={isActive ? onPointerDown : undefined}
            onPointerMove={isActive ? onPointerMove : undefined}
            onPointerUp={isActive ? onPointerUp : undefined}
            onPointerCancel={isActive ? onPointerCancel : undefined}
            aria-hidden={!isActive}
          >
            <span className={`bios-stack-tag bios-stack-tag-${card.tag.toLowerCase()}`}>
              {card.tag}
            </span>
            <h3 className="bios-stack-headline">{card.headline}</h3>
            <p className="bios-stack-subtext">{card.subtext}</p>
          </div>
        );
      })}
      <div className="bios-stack-hint" aria-hidden>
        Tap to investigate · Swipe up
      </div>
      {actionSignal && (
        <div className="bios-signal-actions" role="dialog" aria-label="Signal actions">
          <button type="button" onClick={() => saveSignal(actionSignal)}>
            {savedIds.has(actionSignal.id) ? "Saved" : "Save"}
          </button>
          <button type="button" onClick={() => saveSignal(actionSignal)}>
            Follow
          </button>
          <button type="button" onClick={() => investigateLater(actionSignal)}>
            Investigate later
          </button>
        </div>
      )}
    </div>
  );
}

function stackTransform(depth: number, dragDy: number): string {
  if (depth === 0) {
    const scale = 1 - Math.min(Math.abs(dragDy) / 1200, 0.04);
    return `translate3d(0, ${dragDy}px, 0) scale(${scale})`;
  }
  if (depth === 1) return "translate3d(0, 14px, 0) scale(0.96)";
  return "translate3d(0, 28px, 0) scale(0.92)";
}

function stackOpacity(depth: number): number {
  if (depth === 0) return 1;
  if (depth === 1) return 0.6;
  return 0.3;
}

// ---- Bottom nav --------------------------------------------------------

function BottomNav({
  active,
}: {
  active: "signals" | "command" | "intel" | "search" | "profile";
}) {
  return (
    <nav className="bios-feed-bottomnav" aria-label="Primary navigation">
      <Link
        to="/app"
        className={`bios-feed-bottomnav-tab${active === "signals" ? " active" : ""}`}
      >
        <span className="bios-feed-bottomnav-icon" aria-hidden>◎</span>
        <span className="bios-feed-bottomnav-label">Signals</span>
      </Link>
      <Link
        to="/app/command"
        className={`bios-feed-bottomnav-tab${active === "command" ? " active" : ""}`}
      >
        <span className="bios-feed-bottomnav-icon" aria-hidden>⌘</span>
        <span className="bios-feed-bottomnav-label">Command</span>
      </Link>
      <Link
        to="/app/intel"
        className={`bios-feed-bottomnav-tab${active === "intel" ? " active" : ""}`}
      >
        <span className="bios-feed-bottomnav-icon" aria-hidden>◐</span>
        <span className="bios-feed-bottomnav-label">Intel</span>
      </Link>
      <Link
        to="/app/search"
        className={`bios-feed-bottomnav-tab${active === "search" ? " active" : ""}`}
      >
        <span className="bios-feed-bottomnav-icon" aria-hidden>⌕</span>
        <span className="bios-feed-bottomnav-label">Search</span>
      </Link>
      <Link
        to="/app/profile"
        className={`bios-feed-bottomnav-tab${active === "profile" ? " active" : ""}`}
      >
        <span className="bios-feed-bottomnav-icon" aria-hidden>◌</span>
        <span className="bios-feed-bottomnav-label">Profile</span>
      </Link>
    </nav>
  );
}

// ---- Card sourcing -----------------------------------------------------

function buildSignals(data: OverviewResponse | null): Signal[] {
  if (!data) return [];
  const signals: Signal[] = [];
  const urgencyLevel = data.urgency?.level ?? "low";

  // Priority 1: cross-module priority signals.
  for (const insight of (data.top_insights ?? [])
    .filter((i) => (i.convergence_count ?? 1) > 1 || (i.cross_module_priority_score ?? 0) >= 0.45)
    .slice(0, 3)) {
    const convergence = insight.convergence_count ?? 1;
    signals.push({
      id: `priority-${insight.id}`,
      tag: tagForRec(insight.module, undefined, urgencyLevel),
      headline: capWords(insight.title, 6),
      subtext: capWords(
        convergence > 1
          ? `Converging across ${convergence} modules`
          : insight.why_matters,
        8,
      ),
      module: insight.module,
      query: `Investigate: ${insight.title}`,
    });
  }

  // Priority 2: focus_recommendations.
  for (const r of (data.focus_recommendations ?? []).slice(0, 5)) {
    signals.push({
      id: `rec-${signals.length}`,
      tag: tagForRec(r.module, r.gap_kind, urgencyLevel),
      headline: capWords(r.label, 6),
      subtext: capWords(r.reason, 8),
      module: r.module ?? "system",
      query: r.query,
    });
  }

  // Priority 3: gap_summary.by_kind.
  for (const g of (data.gap_summary?.by_kind ?? []).slice(0, 5)) {
    const isWorkforce = g.modules.includes("workforce");
    const isLegal = g.modules.includes("legal");
    signals.push({
      id: `gap-${g.kind}`,
      tag: isWorkforce ? "WORKFORCE" : isLegal ? "LEGAL" : "GAP",
      headline: capWords(g.what, 6),
      subtext: capWords(g.why, 8),
      module: g.modules[0] ?? "system",
      query: g.query,
    });
  }

  // Priority 4: near_impact.
  for (const n of (data.near_impact ?? []).slice(0, 5)) {
    signals.push({
      id: `near-${n.id}`,
      tag: n.module === "workforce" ? "WORKFORCE" : n.module === "legal" ? "LEGAL" : "MOVEMENT",
      headline: capWords(n.title, 6),
      subtext: capWords(`${n.module} · ${n.impact_state ?? "near"}`, 8),
      module: n.module,
      query: `What outcomes followed this ${n.module} item?`,
    });
  }

  // Dedupe by query so the same recommendation doesn't surface twice
  // when it appears as both a rec and a gap-tied rec.
  const seen = new Set<string>();
  return signals.filter((s) => {
    if (seen.has(s.query)) return false;
    seen.add(s.query);
    return true;
  });
}

function tagForRec(
  module: string | undefined,
  gapKind: string | undefined,
  urgency: string,
): SignalTag {
  if (module === "workforce") return "WORKFORCE";
  if (
    module === "legal" ||
    gapKind?.includes("legal") ||
    gapKind?.includes("rights") ||
    gapKind?.includes("policing") ||
    gapKind?.includes("settlement") ||
    gapKind?.includes("enforcement")
  ) return "LEGAL";
  if (module === "policy" || gapKind?.includes("policy")) return "POLICY";
  if (urgency === "critical") return "CRITICAL";
  return "MOVEMENT";
}

// Trim text to N words; preserve trailing punctuation when possible.
function capWords(text: string, max: number): string {
  if (!text) return "";
  const words = text.trim().split(/\s+/);
  if (words.length <= max) return text.trim();
  return words.slice(0, max).join(" ").replace(/[,;:.]+$/, "") + "…";
}

const SAVED_SIGNALS_KEY = "bios.savedSignals";

function loadSavedSignalIds(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(SAVED_SIGNALS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : []);
  } catch {
    return new Set();
  }
}

function saveSignalIds(ids: Set<string>) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SAVED_SIGNALS_KEY, JSON.stringify([...ids]));
}

// ---- Hook --------------------------------------------------------------

// Phase 36R — mobile means viewport width ≤ 767px and ONLY that.
// Deliberately no navigator.userAgent: a laptop with a narrow window
// should not be classified as mobile by device class, and a
// widescreen-rotated tablet should not be classified as desktop.
// Viewport CSS pixels are the authority.
export function useIsMobile(): boolean {
  const MOBILE_MQ = "(max-width: 767px)";
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(MOBILE_MQ).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(MOBILE_MQ);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isMobile;
}
